import React, { useState, useEffect } from 'react';
import { Container, Snackbar, Alert, Stack, Typography } from '@mui/material';
import DashboardLayout from '../../layout/dashboard/DashboardLayout';
import AiSurveyTable from '../../components/Aisurvey/AiSurveyTable';
import AddQuestion from '../../components/Aisurvey/AddQuestion';
import AddCategory from '../../components/Aisurvey/AddCategory';
import useAuthToken from '../../hooks/getUserData';
import { getCategories, getQuestions } from '../../api/products';
import { questionColumns, categoryColumns } from '../../staticData/tableHead';

const AiSurvey = () => {
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionSearchTerm, setQuestionSearchTerm] = useState('');
  const [categoryPage] = useState(0);
  const [categoryPageSize] = useState(15);
  const [questionPage, setQuestionPage] = useState(0);
  const [questionPageSize] = useState(10);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const token = useAuthToken();

  const formatQuestionType = (type) => {
    switch (type) {
      case 'choice_with_other':
        return 'Choice with other';
      case 'single_line_textbox':
        return 'Single-line textbox';
      case 'multi_line_textbox':
        return 'Multi-line textbox';
      case 'single_choice':
        return 'Single choice';
      case 'url_input':
        return 'Url input';
      default:
        return type
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    }
  };

  const fetchCategories = async (suppressSnackbar = false) => {
    setCategoryLoading(true);
    try {
      const categoryRes = await getCategories(token);
      if (categoryRes.ok) {
        const categoryData = await categoryRes.json();
        setCategories(
          categoryData.map((item) => ({ id: item.id, name: item.name }))
        );
        if (!suppressSnackbar) {
          setSnackbar({
            open: true,
            message: 'Categories fetched successfully',
            severity: 'success',
          });
        }
      } else {
        const errorData = await categoryRes.json().catch(() => ({}));
        setSnackbar({
          open: true,
          message: errorData.error?.message || 'Failed to fetch categories',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching categories',
        severity: 'error',
      });
    } finally {
      setCategoryLoading(false);
    }
  };

  const fetchQuestions = async (suppressSnackbar = false) => {
    setQuestionLoading(true);
    try {
      const questionRes = await getQuestions(token);
      if (questionRes.ok) {
        const questionData = await questionRes.json();
        setQuestions(
          questionData.data.map((item, index) => ({
            id: item.id,
            sno: index + 1,
            questionNo: item.question_number,
            question: item.title,
            questionType: formatQuestionType(item.type),
            questionTypeRaw: item.type,
            category: item.category?.name || '-',
            category_id: item.category?.id || '',
            description: item.description || '',
            options: item.options || [],
            input_label: item.input_label || '',
            input_placeholder: item.input_placeholder || '',
            default_answer: item.default_answer || null,
          }))
        );
        if (!suppressSnackbar) {
          setSnackbar({
            open: true,
            message: 'Questions fetched successfully',
            severity: 'success',
          });
        }
      } else {
        const errorData = await questionRes.json().catch(() => ({}));
        setSnackbar({
          open: true,
          message: errorData.error?.message || 'Failed to fetch questions',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching questions',
        severity: 'error',
      });
    } finally {
      setQuestionLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchCategories(true);
      fetchQuestions(true);
    }
  }, [token]);

  const handleQuestionSearch = (event) => {
    setQuestionSearchTerm(event.target.value);
    setQuestionPage(0);
  };

  const handleQuestionPageChange = (newPage) => {
    setQuestionPage(newPage);
  };

  const addCategory = (categoryData) => {
    setCategories([
      ...categories,
      { id: categories.length + 1, ...categoryData },
    ]);
  };

  const addQuestion = (questionData) => {
    setQuestions([...questions, questionData]);
  };

  const filteredQuestions = questions.filter((question) =>
    question.question.toLowerCase().includes(questionSearchTerm.toLowerCase())
  );

  const totalQuestionPages = Math.ceil(
    filteredQuestions.length / questionPageSize
  );

  return (
    <DashboardLayout>
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom>AI Survey</Typography>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        <Stack spacing={3}>
          <AiSurveyTable
            tableName="Category"
            columnFields={categoryColumns}
            filteredData={categories.map((cat, index) => ({
              id: cat.id,
              sno: index + 1,
              category: cat.name,
            }))}
            fetchData={() => fetchCategories(true)}
            addModal={
              <AddCategory
                onAddCategory={addCategory}
                setMessage={(msg) =>
                  setSnackbar({
                    open: true,
                    message: msg,
                    severity: 'success',
                  })
                }
                setSnackbarOpen={(open) =>
                  setSnackbar((prev) => ({ ...prev, open }))
                }
                fetchCategories={() => fetchCategories(true)}
              />
            }
            setMessage={(msg) =>
              setSnackbar({
                open: true,
                message: msg,
                severity: 'success',
              })
            }
            setSnackbarOpen={(open) =>
              setSnackbar((prev) => ({ ...prev, open }))
            }
            page={categoryPage}
            pageSize={categoryPageSize}
            loading={categoryLoading}
            showSearch={false}
            showPagination={false}
          />

          <AiSurveyTable
            tableName="Question"
            columnFields={questionColumns}
            filteredData={filteredQuestions}
            fetchData={() => fetchQuestions(true)}
            addModal={
              <AddQuestion
                onAddQuestion={addQuestion}
                categories={categories}
                fetchQuestions={() => fetchQuestions(true)}
                setMessage={(msg) =>
                  setSnackbar({
                    open: true,
                    message: msg,
                    severity: 'success',
                  })
                }
                setSnackbarOpen={(open) =>
                  setSnackbar((prev) => ({ ...prev, open }))
                }
              />
            }
            setMessage={(msg) =>
              setSnackbar({
                open: true,
                message: msg,
                severity: 'success',
              })
            }
            setSnackbarOpen={(open) =>
              setSnackbar((prev) => ({ ...prev, open }))
            }
            page={questionPage}
            pageSize={questionPageSize}
            loading={questionLoading}
            searchTerm={questionSearchTerm}
            handleSearch={handleQuestionSearch}
            showSearch={true}
            categories={categories}
            showPagination={true}
            totalPages={totalQuestionPages}
            onPageChange={handleQuestionPageChange}
          />
        </Stack>
      </Container>
    </DashboardLayout>
  );
};

export default AiSurvey;
